/* eslint-disable max-lines */
import {
  createContext,
  type Dispatch,
  type SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import { chatWithAnnieBackend } from '@/lib/queries/chat';

import { useWebSockets } from './WebsocketsProvider';

export type ChatThinking = {
  content?: string;
  messageId: string;
};

export type Chat = {
  messages: {
    content: string;
    sent: boolean;
    error?: string;
    messageId: string;
  }[];
  thinking: ChatThinking | undefined;
};

export type ChatContextType = {
  chatWithAnnie: ({
    message,
    projectId,
  }: {
    message: string;
    projectId: string;
  }) => void;
  prompt: string;
  setPrompt: Dispatch<SetStateAction<string>>;
};

const ChatContext = createContext<ChatContextType | null>(null);

export const ChatProvider = ({
  setChat,
  children,
  chat,
}: {
  setChat: Dispatch<SetStateAction<Chat>>;
  children: React.ReactNode;
  chat: Chat;
}): JSX.Element => {
  const [conversationId] = useState(crypto.randomUUID());
  const [prompt, setPrompt] = useState('');
  const pushMessage = useCallback(
    (message: {
      content: string;
      sent: boolean;
      error?: string;
      messageId: string;
    }) => {
      setChat(prev => {
        return {
          ...prev,
          messages: [...prev.messages, message],
        };
      });
    },
    [setChat],
  );

  const setThinking = useCallback(
    (thinking: ChatThinking | undefined) => {
      setChat(prev => ({
        ...prev,
        thinking,
      }));
    },
    [setChat],
  );

  const chatWithAnnie = useCallback(
    ({ message, projectId }: { message: string; projectId: string }) => {
      const messageId = crypto.randomUUID();
      pushMessage({
        content: message,
        sent: true,
        messageId,
      });
      setThinking({ messageId });

      const history = [
        ...chat.messages,
        {
          content: message,
          sent: true,
        },
      ].map(m => ({
        content: m.content,
        role: m.sent ? ('user' as const) : ('assistant' as const),
      }));

      void chatWithAnnieBackend({
        projectId,
        prompt: message,
        conversationId,
        history,
        messageId,
      });
    },
    [pushMessage, setThinking, conversationId, chat],
  );

  const annieResponse = useCallback(
    (message: string, messageId: string) => {
      pushMessage({
        content: message,
        sent: false,
        error: undefined,
        messageId,
      });
      setThinking(undefined);
    },
    [pushMessage, setThinking],
  );

  const annieError = useCallback(
    (message: string, messageId: string) => {
      pushMessage({
        content: message,
        sent: false,
        error: message,
        messageId,
      });
      setThinking(undefined);
    },
    [pushMessage, setThinking],
  );

  const { addMessageHandler, removeMessageHandler } = useWebSockets();

  useEffect(() => {
    addMessageHandler('chat', (content: { content: string }) => {
      const parsedContent = JSON.parse(content.content) as {
        result: string;
        prompt: string;
        conversationId: string;
        messageId: string;
      };

      if (parsedContent.conversationId !== conversationId) {
        return;
      }

      annieResponse(parsedContent.result, parsedContent.messageId);
    });

    return () => {
      removeMessageHandler('chat');
    };
  }, [annieResponse, addMessageHandler, removeMessageHandler, conversationId]);

  useEffect(() => {
    addMessageHandler('chat-error', (content: { content: string }) => {
      const parsedContent = JSON.parse(content.content) as {
        error: string;
        conversationId: string;
        messageId: string;
      };

      if (parsedContent.conversationId !== conversationId) {
        return;
      }

      annieError(parsedContent.error, parsedContent.messageId);
    });

    return () => {
      removeMessageHandler('chat-error');
    };
  }, [addMessageHandler, annieError, removeMessageHandler, conversationId]);

  useEffect(() => {
    addMessageHandler('thinking', ({ content }: { content: string }) => {
      const parsedContent = JSON.parse(content) as {
        content: string;
        messageId: string;
        conversationId: string;
      };

      if (parsedContent.conversationId !== conversationId) {
        return;
      }

      setThinking({
        content: parsedContent.content,
        messageId: parsedContent.messageId,
      });
    });

    return () => {
      removeMessageHandler('thinking');
    };
  }, [addMessageHandler, removeMessageHandler, setThinking, conversationId]);

  return (
    <ChatContext.Provider
      value={{
        chatWithAnnie,
        prompt,
        setPrompt,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

export const useChat = (): Pick<
  ChatContextType,
  'chatWithAnnie' | 'prompt' | 'setPrompt'
> => {
  const chatContext = useContext(ChatContext);
  if (chatContext === null) {
    throw new Error('useChat must be used within a ChatProvider');
  }

  return {
    chatWithAnnie: chatContext.chatWithAnnie,
    prompt: chatContext.prompt,
    setPrompt: chatContext.setPrompt,
  };
};
